import { createElement } from 'react';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    dynamicWidgetEventSourceSchema,
    DynamicWidgetModelBuilder,
    dynamicWidgetResourceSourceSchema,
    type GenericTableWidgetModel,
    genericTableWidgetModelSchema,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';
import {
    useAgentRunWorkspaceFileRows,
    WORKSPACE_FILE_PAGE_SIZE,
} from '@apps/web/hooks/use_agent_run_workspace_file_rows';
import {
    type GenericTableReceipt,
    parseGenericTableReceipt,
} from '@apps/web/lib/agent_render_artifact';

const WORKSPACE_FILE_API_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/workspace-files/{file_path}';

function parseTableReceipt(entry: ToolExecutionEntry): {
    readonly receipt: GenericTableReceipt;
    readonly workspacePath: string;
} | null {
    return parseGenericTableReceipt(entry.name, entry.output);
}

export function matchesGenericTable(entry: ToolExecutionEntry): boolean {
    return parseTableReceipt(entry) !== null;
}

export function buildGenericTableWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): GenericTableWidgetModel | null {
    if (
        entry.callId === null ||
        !entry.toolCallEventId ||
        !entry.id ||
        !entry.runId
    ) {
        return null;
    }
    const artifact = parseTableReceipt(entry);
    if (artifact === null) {
        return null;
    }
    const eventSource = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const resourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'agent_run_workspace_file',
        resourceId: `${entry.runId}:${artifact.workspacePath}`,
        apiPath: WORKSPACE_FILE_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const eventSourceId = getDynamicWidgetSourceId(eventSource);
    const resourceSourceId = getDynamicWidgetSourceId(resourceSource);
    const builder = new DynamicWidgetModelBuilder([
        eventSource,
        resourceSource,
    ]);
    builder.addSourceField({
        id: 'table.receipt',
        sourceId: eventSourceId,
        path: '/render_artifact',
        rawValue: artifact.receipt,
        value: artifact.receipt,
        renderedValue: `${artifact.receipt.row_count} rows`,
        presentation: buildBindingPresentation({
            section: 'table',
            label: 'receipt',
        }),
    });
    builder.addLiveField({
        id: 'resource.rows',
        sourceIds: [resourceSourceId],
        paths: ['/rows'],
        presentation: buildBindingPresentation({
            section: 'table',
            label: 'rows',
        }),
    });
    return genericTableWidgetModelSchema.parse({
        ...builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'table'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'table',
        templateVersion: 1,
        columns: artifact.receipt.columns,
        columnsTruncated: artifact.receipt.columns_truncated,
        rows: [],
        rowCount: artifact.receipt.row_count,
        byteSize: artifact.receipt.byte_size,
        format: artifact.receipt.format,
        suggestedComponent: artifact.receipt.suggested_component ?? null,
        rowProvenance: 'resource',
        workspaceFile: {
            organizationId: context.organizationId,
            runId: entry.runId,
            path: artifact.workspacePath,
            eventId: entry.id,
        },
    });
}

function GenericTableWidget({
    model,
}: {
    readonly model: GenericTableWidgetModel;
}) {
    const workspaceRows = useAgentRunWorkspaceFileRows({
        organizationId: model.workspaceFile.organizationId,
        runId: model.workspaceFile.runId,
        filePath: model.workspaceFile.path,
        format: model.format,
        eventId: model.workspaceFile.eventId,
    });
    const rows = workspaceRows.pageRows ?? workspaceRows.rows;
    return (
        <section className="w-full overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(0,36,41,0.06),0_8px_24px_rgba(0,36,41,0.06)] ring-1 ring-[#DBE3E3]">
            <header className="flex items-center justify-between gap-3 border-b border-[#E8EEEE] px-4 py-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-balance text-[#002429]">
                        Agent-produced table
                    </h3>
                    <p className="text-xs text-[#617579] tabular-nums">
                        {workspaceRows.isLoading
                            ? 'Loading rows…'
                            : `${model.rowCount} row${model.rowCount === 1 ? '' : 's'}`}
                    </p>
                    {model.columnsTruncated ? (
                        <p className="text-xs text-[#8A5A12]">
                            Some columns are hidden.
                        </p>
                    ) : null}
                </div>
                <span className="rounded-full bg-[#F0F5F5] px-2 py-1 text-[11px] font-medium tracking-wide text-[#42595E] uppercase">
                    {model.format}
                </span>
            </header>
            {workspaceRows.error !== undefined ? (
                <p className="px-4 py-4 text-sm text-[#8A2E2E]">
                    This table could not be loaded.
                </p>
            ) : (
                <div className="max-h-[420px] overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-[#F7FAFA] text-left text-[#42595E]">
                            <tr>
                                {model.columns.map((column) => (
                                    <th
                                        className="px-3 py-2 font-medium whitespace-nowrap"
                                        key={column}
                                    >
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr
                                    className="border-t border-[#E8EEEE]"
                                    key={rowIndex}
                                >
                                    {model.columns.map((column) => (
                                        <td
                                            className="max-w-[240px] px-3 py-2 align-top break-words text-[#23383C]"
                                            key={column}
                                        >
                                            {formatCell(row[column])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <footer className="flex items-center justify-between border-t border-[#E8EEEE] px-3 py-2 text-xs text-[#617579]">
                <span>
                    Page {(workspaceRows.pageIndex ?? 0) + 1}
                    {model.rowCount > 0
                        ? ` of ${Math.ceil(model.rowCount / WORKSPACE_FILE_PAGE_SIZE)}`
                        : ''}
                </span>
                <span className="flex gap-2">
                    <button
                        className="rounded border border-[#C9D5D5] px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={
                            !workspaceRows.hasPrevious ||
                            workspaceRows.isLoadingMore
                        }
                        onClick={workspaceRows.loadPreviousPage}
                        type="button"
                    >
                        Previous
                    </button>
                    <button
                        className="rounded border border-[#C9D5D5] px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={
                            !workspaceRows.hasMore ||
                            workspaceRows.isLoadingMore
                        }
                        onClick={workspaceRows.loadNextPage}
                        type="button"
                    >
                        Next
                    </button>
                </span>
            </footer>
        </section>
    );
}

function formatCell(value: unknown): string {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'string') {
        return value;
    }
    return JSON.stringify(value) ?? '';
}

export const genericTableAdapter: DynamicWidgetAdapter<GenericTableWidgetModel> =
    {
        template: 'table',
        templateVersion: 1,
        toolNames: new Set(['render_dynamic_ui']),
        matches: matchesGenericTable,
        build: buildGenericTableWidgetModel,
        render(model) {
            return createElement(GenericTableWidget, { model });
        },
    };
